import { createFileRoute, Link } from "@tanstack/react-router";
import { createServerFn } from "@tanstack/react-start";
import { createClient } from "@supabase/supabase-js";
import type { Database } from "@/integrations/supabase/types";
import { useEffect, useState } from "react";
import { SiteNav } from "@/components/site-nav";
import { SiteFooter } from "@/components/site-footer";
import { SectionNav } from "@/components/section-nav";
import { supabase } from "@/integrations/supabase/client";

type NL = { id: string; subject: string; body_html: string | null; sent_at: string | null };

type NLMeta = { subject: string | null } | null;

const fetchNewsletterMeta = createServerFn({ method: "GET" })
  .inputValidator((data: { id: string }) => data)
  .handler(async ({ data }): Promise<NLMeta> => {
    const supa = createClient<Database>(
      process.env.SUPABASE_URL!,
      process.env.SUPABASE_PUBLISHABLE_KEY!,
      { auth: { storage: undefined, persistSession: false, autoRefreshToken: false } },
    );
    const { data: row } = await supa
      .from("newsletters")
      .select("subject")
      .eq("id", data.id)
      .maybeSingle();
    return (row as NLMeta) ?? null;
  });

export const Route = createFileRoute("/comunicaciones/newsletter/$id")({
  loader: ({ params }) => fetchNewsletterMeta({ data: { id: params.id } }),
  head: ({ params, loaderData }) => {
    const subject = loaderData?.subject ?? "Newsletter";
    const desc = `${subject} — edición del newsletter Mine Class con novedades del ecosistema minero.`;
    const url = `https://mine-class.com/comunicaciones/newsletter/${params.id}`;
    return {
      meta: [
        { title: `${subject} | Newsletter | Mine Class` },
        { name: "description", content: desc },
        { property: "og:title", content: subject },
        { property: "og:description", content: desc },
        { property: "og:url", content: url },
        { property: "og:type", content: "article" },
      ],
      links: [{ rel: "canonical", href: url }],
    };
  },
  component: Page,
});

function Page() {
  const { id } = Route.useParams();
  const [n, setN] = useState<NL | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      const { data } = await supabase
        .from("newsletters")
        .select("id,subject,body_html,sent_at")
        .eq("id", id)
        .maybeSingle();
      setN((data ?? null) as NL | null);
      setLoading(false);
    })();
  }, [id]);

  return (
    <div className="bg-white min-h-screen flex flex-col">
      <SiteNav />
      <SectionNav section="comunicaciones" />
      <main className="flex-1 max-w-5xl mx-auto px-margin-desktop py-xl w-full">
        <Link to="/comunicaciones/newsletter" className="text-sm underline text-[var(--color-brand-purple)]">
          ← Volver a Newsletter
        </Link>
        {loading ? (
          <p className="mt-8 text-[var(--color-on-surface-variant)]">Cargando…</p>
        ) : !n ? (
          <p className="mt-8 text-[var(--color-on-surface-variant)]">Edición no encontrada.</p>
        ) : (
          <>
            <div className="mt-6 mb-4">
              <div className="text-xs uppercase tracking-widest text-[var(--color-brand-coral)] font-bold">Newsletter</div>
              <h1 className="text-3xl font-bold text-[var(--color-brand-purple)] mt-1">{n.subject}</h1>
              {n.sent_at && (
                <p className="text-sm text-[var(--color-on-surface-variant)] mt-2">
                  {new Date(n.sent_at).toLocaleDateString("es-CL", { dateStyle: "long" })}
                </p>
              )}
            </div>
            <iframe
              title={n.subject}
              srcDoc={n.body_html ?? ""}
              className="w-full border border-[var(--color-outline-variant)] rounded"
              style={{ height: "80vh", minHeight: 600 }}
            />
          </>
        )}
      </main>
      <SiteFooter />
    </div>
  );
}
